/*
  BURme likes
  Saves liked products in the shopper's browser (localStorage) and keeps every
  heart button, the header icon and the Likes page in sync.
  Settings live in "BURme header styling" (header group) and the blocks/sections
  that show hearts.
*/
(() => {
  if (window.BurmeLikes) return;

  const KEY = 'burme-likes-v1';
  const MAX = 100;
  const HEART =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.3-9.2C1.5 8.1 3.4 4.5 7 4.5c2 0 3.6 1.1 5 2.9 1.4-1.8 3-2.9 5-2.9 3.6 0 5.5 3.6 4.3 6.8-1.8 4.6-9.3 9.2-9.3 9.2z"/></svg>';

  // Header heart drawn on the same 20x20 grid and stroke width as Horizon's header icons
  const HEADER_HEART =
    '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path stroke="currentColor" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M10 16.1s-5.6-3.5-6.8-6.95C2.45 6.8 3.85 4.3 6.4 4.3c1.5 0 2.7.85 3.6 2.1.9-1.25 2.1-2.1 3.6-2.1 2.55 0 3.95 2.5 3.2 4.85C15.6 12.6 10 16.1 10 16.1z"/></svg>';

  const cfg = () => window.BurmeLikesConfig || {};

  /* ---------- storage ---------- */
  const read = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && typeof item.h === 'string' && item.h) : [];
    } catch (error) {
      return [];
    }
  };
  const write = (list) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (error) {
      /* storage full or blocked - ignore */
    }
  };
  const handles = () => read().map((item) => item.h);
  const has = (handle) => handles().includes(handle);
  const emit = () => {
    document.dispatchEvent(new CustomEvent('burme:likes-changed', { detail: { handles: handles() } }));
  };
  const add = (handle) => {
    if (!handle || has(handle)) return;
    const list = read();
    list.unshift({ h: handle, t: Date.now() });
    write(list);
    emit();
  };
  const remove = (handle) => {
    write(read().filter((item) => item.h !== handle));
    emit();
  };
  const toggle = (handle) => {
    if (has(handle)) remove(handle);
    else add(handle);
    return has(handle);
  };
  const clear = () => {
    write([]);
    emit();
  };
  const handleFromUrl = (url) => {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  /* ---------- keep buttons + counts in sync ---------- */
  const setText = (el, text) => {
    if (el.textContent !== text) el.textContent = text;
  };
  const sync = () => {
    const liked = new Set(handles());
    const config = cfg();
    document.querySelectorAll('[data-burme-like]').forEach((btn) => {
      const on = liked.has(btn.dataset.burmeLike);
      const pressed = on ? 'true' : 'false';
      if (btn.getAttribute('aria-pressed') !== pressed) btn.setAttribute('aria-pressed', pressed);
      btn.classList.toggle('is-liked', on);
      const label = on
        ? btn.dataset.labelOn || config.labelOn || 'Remove from likes'
        : btn.dataset.labelOff || config.labelOff || 'Add to likes';
      if (btn.getAttribute('aria-label') !== label) btn.setAttribute('aria-label', label);
      const text = btn.querySelector('.burme-like__text');
      if (text) setText(text, label);
    });
    const count = liked.size;
    document.querySelectorAll('[data-burme-likes-count]').forEach((el) => {
      setText(el, count > 99 ? '99+' : String(count));
      if (el.hidden !== (count === 0)) el.hidden = count === 0;
    });
    document.querySelectorAll('.burme-likes-header').forEach((el) => el.classList.toggle('has-likes', count > 0));
  };

  /* ---------- clicks ---------- */
  document.addEventListener(
    'click',
    (event) => {
      const clearBtn = event.target.closest && event.target.closest('[data-burme-likes-clear]');
      if (clearBtn) {
        event.preventDefault();
        clear();
        return;
      }
      const btn = event.target.closest && event.target.closest('[data-burme-like]');
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(btn.dataset.burmeLike);
      btn.classList.remove('is-popping');
      void btn.offsetWidth;
      btn.classList.add('is-popping');
    },
    true
  );
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const btn = event.target.closest && event.target.closest('span[data-burme-like]');
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      btn.click();
    },
    true
  );

  /* ---------- hearts added by script ---------- */
  const makeButton = (handle, className) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.dataset.burmeLike = handle;
    btn.setAttribute('aria-pressed', 'false');
    btn.innerHTML = HEART;
    return btn;
  };

  // Horizon product cards (collection pages, search, recommendations)
  const decorateCards = () => {
    const config = cfg();
    if (!config.cardHearts) return;
    document.querySelectorAll('product-card .card-gallery').forEach((gallery) => {
      if (gallery.querySelector(':scope > .burme-like')) return;
      const link =
        gallery.querySelector('a[href*="/products/"]') ||
        gallery.closest('product-card')?.querySelector('a[href*="/products/"]');
      const handle = handleFromUrl(link && link.getAttribute('href'));
      if (!handle) return;
      gallery.appendChild(
        makeButton(handle, 'burme-like burme-like--overlay burme-like--card burme-like--' + (config.cardCorner || 'top-right'))
      );
    });
  };

  // Product page: heart on the main image (template rendered by the Media block)
  const decorateGallery = () => {
    document.querySelectorAll('template[data-burme-gallery-like]').forEach((template) => {
      const scope = template.parentElement;
      const gallery = scope && scope.querySelector('media-gallery');
      const source = template.content.firstElementChild;
      if (!gallery || !source) return;
      const targets = [];
      const firstGridItem = gallery.querySelector('.media-gallery__grid > .product-media-container:first-child');
      if (firstGridItem) targets.push(firstGridItem);
      const slideshow = gallery.querySelector(':scope slideshow-component');
      if (slideshow) targets.push(slideshow);
      targets.forEach((target) => {
        if (target.querySelector(':scope > .burme-like')) return;
        target.classList.add('burme-like-host');
        target.appendChild(source.cloneNode(true));
      });
    });
  };

  // Header icon next to the cart
  const decorateHeader = () => {
    const config = cfg();
    if (!config.headerIcon) return;
    document.querySelectorAll('header-actions').forEach((actions) => {
      if (actions.querySelector('.burme-likes-header')) return;
      const link = document.createElement('a');
      link.className = 'header-actions__action burme-likes-header';
      link.href = config.url || '/pages/likes';
      link.setAttribute('aria-label', config.headerLabel || 'Likes');
      link.innerHTML =
        '<span class="burme-likes-header__inner">' +
        '<span class="svg-wrapper burme-likes-header__icon" aria-hidden="true">' +
        HEADER_HEART +
        '</span>' +
        (config.headerCount ? '<span class="burme-likes-header__count" data-burme-likes-count aria-hidden="true" hidden></span>' : '') +
        '</span>';
      const cart = actions.querySelector('cart-icon');
      const cartAction = cart ? cart.closest('.header-actions__action') : null;
      actions.insertBefore(link, cartAction && cartAction.parentElement === actions ? cartAction : null);
    });
  };

  /* ---------- Likes page ---------- */
  const productCache = new Map();
  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productCache.set(
        handle,
        fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };
  const withDelimiters = (number, decimals, thousands, decimal) => {
    const parts = number.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };
  const formatMoney = (cents, format) => {
    const amount = Number(cents || 0) / 100;
    return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(amount, 2, "'", '.');
        default:
          return withDelimiters(amount, 2, ',', '.');
      }
    });
  };
  const sizedImage = (url, width) => {
    if (!url) return '';
    const src = url.startsWith('//') ? 'https:' + url : url;
    return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
  };
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  const renderPage = async (page) => {
    const grid = page.querySelector('[data-burme-likes-grid]');
    const empty = page.querySelector('[data-burme-likes-empty]');
    const toolbar = page.querySelector('[data-burme-likes-toolbar]');
    if (!grid || !empty) return;
    const showEmpty = (isEmpty) => {
      empty.hidden = !isEmpty;
      grid.hidden = isEmpty;
      if (toolbar) toolbar.hidden = isEmpty;
    };
    const list = handles();
    const token = String(Math.random());
    page.dataset.renderToken = token;
    if (!list.length) {
      grid.replaceChildren();
      showEmpty(true);
      page.classList.remove('is-loading');
      return;
    }
    page.classList.add('is-loading');
    const products = await Promise.all(list.map(fetchProduct));
    if (page.dataset.renderToken !== token) return;
    const format = (page.dataset.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
    const soldOut = page.dataset.soldOutText || 'Sold out';
    const cards = [];
    products.forEach((product, index) => {
      if (!product) return;
      const handle = list[index];
      const url = product.url || '/products/' + handle;
      const card = el('div', 'burme-likes__card');

      const media = el('a', 'burme-likes__media');
      media.href = url;
      if (product.featured_image) {
        const img = el('img');
        img.src = sizedImage(product.featured_image, 700);
        img.alt = product.title || '';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      card.appendChild(media);

      const heart = makeButton(handle, 'burme-like burme-like--overlay burme-like--card burme-like--top-right');
      card.appendChild(heart);

      const info = el('div', 'burme-likes__info');
      const title = el('a', 'burme-likes__title', product.title || '');
      title.href = url;
      info.appendChild(title);
      const price = el('div', 'burme-likes__price');
      price.appendChild(el('span', 'burme-likes__price-now', formatMoney(product.price, format)));
      if (product.compare_at_price && product.compare_at_price > product.price) {
        price.appendChild(el('s', 'burme-likes__price-was', formatMoney(product.compare_at_price, format)));
      }
      info.appendChild(price);
      if (product.available === false) info.appendChild(el('div', 'burme-likes__soldout', soldOut));
      card.appendChild(info);
      cards.push(card);
    });
    grid.replaceChildren(...cards);
    showEmpty(cards.length === 0);
    page.classList.remove('is-loading');
    sync();
  };
  const renderPages = () => {
    document.querySelectorAll('[data-burme-likes-page]').forEach((page) => renderPage(page));
  };

  /* ---------- start ---------- */
  const run = () => {
    decorateHeader();
    decorateCards();
    decorateGallery();
    sync();
  };
  let frame = null;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      run();
    });
  };
  document.addEventListener('burme:likes-changed', () => {
    sync();
    renderPages();
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    sync();
    renderPages();
  });
  document.addEventListener('shopify:section:load', () => {
    run();
    renderPages();
  });
  const start = () => {
    run();
    renderPages();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.BurmeLikes = { list: handles, has, add, remove, toggle, clear, refresh: run };
})();
